import type { DocumentView, FieldCatalogueEntry, RequestFieldView, ValueSource } from "@procuremate/shared";
import { clsx } from "clsx";
import { Building2, Cog, FileText, type LucideIcon, MessageSquareQuote, Plus } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { Language } from "@/i18n";
import { useResolveConflict } from "./api";
import { documentDownloadUrl } from "./documents-api";
import { FieldEditor } from "./field-editor";
import { displayValue } from "./field-format";
import { usePanelActions } from "./panel-actions";

const SOURCE_ICONS: Record<ValueSource, LucideIcon> = {
  REQUESTER: MessageSquareQuote,
  DOCUMENT: FileText,
  DIRECTORY: Building2,
  SYSTEM: Cog,
};

/**
 * One element (WP-2.8): label and value on one line (stacked when the panel is narrow), the source
 * as a small icon that opens what the value was recorded from, and the value itself as the way to
 * edit it. A document's different value is shown beside the requester's, for them to pick one.
 */
export function FieldRow({
  requestId,
  fieldKey,
  entry,
  field,
  canEdit,
  editBlocked,
  required = false,
  skippable = false,
  highlighted = false,
  openEditor = 0,
  documents = [],
  onSaved = () => undefined,
  audience = "requester",
}: {
  requestId: string;
  fieldKey: string;
  entry: FieldCatalogueEntry | undefined;
  field: RequestFieldView | undefined;
  canEdit: boolean;
  editBlocked: boolean;
  /** Open and not skippable: needed before the request can be submitted at all. */
  required?: boolean;
  /** Open, mandatory, but "Send anyway" may record it as not provided. */
  skippable?: boolean;
  /** Changed by the latest turn (WP-2.8). */
  highlighted?: boolean;
  /** Changes (a counter) when "Fix it" asks this row to open its editor. */
  openEditor?: number;
  documents?: DocumentView[];
  onSaved?: (fieldKey: string) => void;
  /** Whose words "From you" means: the buyer's review page says "From the requester" (WP-3.1). */
  audience?: "requester" | "buyer";
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const labelId = useId();
  const rowRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const label = entry?.labels[language] ?? fieldKey;
  const value = field ? displayValue(field, entry, language, t) : null;
  const editable = canEdit && Boolean(entry?.editable);
  const hasValue = field?.status === "CAPTURED";
  const empty = value === null || (Array.isArray(value) ? value.length === 0 : value === "");

  // "Fix it" (Before you send): open this row's editor and bring it into view.
  useEffect(() => {
    if (openEditor === 0 || !editable) return;
    setEditing(true);
    rowRef.current?.scrollIntoView({ block: "center" });
  }, [openEditor, editable]);

  function close(saved: boolean) {
    setEditing(false);
    if (saved) onSaved(fieldKey);
    // Cancelled: the row stays where it is; its button re-renders after the editor unmounts.
    else requestAnimationFrame(() => button.current?.focus());
  }

  const valueText = Array.isArray(value) ? (
    value.map((item, i) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: items are plain text and may repeat.
      <span key={i} className="block break-words before:content-['•_']">
        {item}
      </span>
    ))
  ) : (
    <span className="whitespace-pre-wrap break-words">{value}</span>
  );

  return (
    <div
      ref={rowRef}
      data-field-row={fieldKey}
      className={clsx("rounded-sm py-2 transition-colors duration-700", highlighted && "bg-warning-bg")}
    >
      <div className="grid gap-x-3 gap-y-0.5 @min-[22rem]:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <p className="min-w-0 text-sm font-medium break-words text-fg">
          {/* The editor is named by the label alone, not by the tags beside it. */}
          <span id={labelId}>{label}</span>
          {required ? <Tag tone="warning">{t("requests.submit.requiredTag")}</Tag> : null}
          {skippable ? <Tag tone="muted">{t("requests.panel.canSkip")}</Tag> : null}
          {highlighted ? <Tag tone="muted">{t("requests.panel.updated")}</Tag> : null}
        </p>
        <div className="min-w-0">
          {editing && entry ? (
            <FieldEditor requestId={requestId} entry={entry} field={field} labelId={labelId} onClose={close} />
          ) : (
            <div className="flex min-w-0 items-start gap-1">
              {editable ? (
                <button
                  ref={button}
                  type="button"
                  data-field-button={fieldKey}
                  data-has-value={hasValue}
                  disabled={editBlocked}
                  title={editBlocked ? t("requests.panel.busyEditing") : undefined}
                  aria-label={empty ? t("requests.panel.addField", { field: label }) : undefined}
                  className={clsx(
                    "min-h-8 min-w-0 flex-1 rounded-sm px-1.5 py-1 text-left text-sm hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60",
                    empty ? "inline-flex items-center gap-1 font-semibold text-link" : "text-fg",
                  )}
                  onClick={() => setEditing(true)}
                >
                  {empty ? (
                    <>
                      <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                      {t("requests.panel.add")}
                    </>
                  ) : (
                    <>
                      <span className="sr-only">{`${t("requests.panel.editValue", { field: label })}:`}</span>{" "}
                      {valueText}
                    </>
                  )}
                </button>
              ) : empty ? (
                entry && !entry.editable ? (
                  <p className="text-sm text-fg-muted">{t("requests.panel.automatic")}</p>
                ) : null
              ) : (
                <p className="min-w-0 flex-1 text-sm text-fg">{valueText}</p>
              )}
              {field && !empty && audience === "requester" ? (
                <SourceTag field={field} label={label} requestId={requestId} documents={documents} />
              ) : null}
            </div>
          )}
          {/* The buyer's wide page keeps the source in words; "not provided" says it all. */}
          {field && !empty && audience === "buyer" && field.status !== "NOT_PROVIDED" ? (
            <p className="mt-0.5 text-xs text-fg-muted">{t(`review.source.${field.source}`)}</p>
          ) : null}
        </div>
      </div>
      {field?.documentValue && !editing ? (
        <DocumentDisagreement
          requestId={requestId}
          field={field}
          entry={entry}
          documents={documents}
          canChoose={editable && !editBlocked}
          audience={audience}
        />
      ) : null}
    </div>
  );
}

function Tag({ tone, children }: { tone: "warning" | "muted"; children: string }) {
  return (
    <span
      className={clsx(
        "ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-semibold",
        tone === "warning" ? "bg-warning-bg text-warning-fg" : "bg-bg text-fg-muted",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Where a value came from (WP-2.8): an icon that opens the words it was recorded from, and a way to
 * them — the requester's message in the conversation, or the document to download.
 */
function SourceTag({
  field,
  label,
  requestId,
  documents,
}: {
  field: RequestFieldView;
  label: string;
  requestId: string;
  documents: DocumentView[];
}) {
  const { t } = useTranslation();
  const actions = usePanelActions();
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const Icon = SOURCE_ICONS[field.source];
  const sourceText = t(`requests.panel.source.${field.source}`);
  const document = field.documentId ? documents.find((d) => d.id === field.documentId) : undefined;

  function close() {
    setOpen(false);
    toggle.current?.focus();
  }
  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  }

  return (
    <div className="relative shrink-0">
      <button
        ref={toggle}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t("requests.panel.sourceButton", { field: label, source: sourceText })}
        title={sourceText}
        className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-fg-muted hover:bg-bg hover:text-primary"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon aria-hidden="true" className="h-4 w-4" />
      </button>
      {open ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: Escape closes the disclosure; its controls are buttons and links.
        <div
          id={panelId}
          onKeyDown={onKeyDown}
          className="absolute top-8 right-0 z-20 w-72 max-w-[80vw] rounded-md border border-border bg-surface p-3 text-left shadow-card"
        >
          <p className="text-xs font-semibold text-fg-muted">{sourceText}</p>
          {field.evidence && (field.source === "REQUESTER" || field.source === "DOCUMENT") ? (
            <>
              <p className="mt-1 text-xs text-fg-muted">{t("requests.panel.quoted")}</p>
              <blockquote className="mt-0.5 border-l-2 border-border pl-2 text-sm break-words text-fg">
                “{field.evidence}”
              </blockquote>
            </>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {field.source === "REQUESTER" && field.messageId && actions ? (
              <Button
                variant="secondary"
                className="h-8 px-2.5"
                onClick={() => {
                  setOpen(false);
                  if (field.messageId) actions.showMessage(field.messageId);
                }}
              >
                {t("requests.panel.showInConversation")}
              </Button>
            ) : null}
            {document ? (
              <a
                href={documentDownloadUrl(requestId, document.id)}
                className="inline-flex h-8 items-center rounded-md border border-border px-2.5 text-sm font-semibold text-primary hover:bg-bg"
              >
                {t("requests.panel.openDocument", { name: document.fileName })}
              </a>
            ) : null}
            <Button variant="ghost" className="h-8 px-2.5" onClick={close}>
              {t("requests.panel.closeSource")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The requester's value and a document's different one (WP-2.8): the requester keeps theirs or takes the document's. */
function DocumentDisagreement({
  requestId,
  field,
  entry,
  documents,
  canChoose,
  audience,
}: {
  requestId: string;
  field: RequestFieldView;
  entry: FieldCatalogueEntry | undefined;
  documents: DocumentView[];
  canChoose: boolean;
  audience: "requester" | "buyer";
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const resolve = useResolveConflict(requestId);
  const other = field.documentValue;
  if (!other) return null;
  const name = documents.find((d) => d.id === other.documentId)?.fileName ?? t("requests.panel.aDocument");
  const show = (v: ReturnType<typeof displayValue>) => (Array.isArray(v) ? v.join("; ") : v);
  const mine = show(displayValue(field, entry, language, t));
  const theirs = show(displayValue({ value: other.value, status: "CAPTURED" }, entry, language, t));

  if (audience === "buyer") {
    return (
      <p className="mt-1 text-xs text-fg-muted">
        {t("review.conflict", { name })} <span className="text-fg">{theirs}</span>
      </p>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-border border-l-4 border-l-warning-fg bg-bg p-3">
      <p className="text-sm font-semibold text-fg">{t("requests.panel.conflict.title")}</p>
      <dl className="mt-1 grid gap-x-3 text-sm @min-[30rem]:grid-cols-[auto_minmax(0,1fr)] @min-[30rem]:gap-y-1">
        <dt className="text-fg-muted">{t("requests.panel.conflict.yours")}</dt>
        <dd className="mb-1 break-words text-fg @min-[30rem]:mb-0">{mine}</dd>
        <dt className="text-fg-muted">{t("requests.panel.conflict.document", { name })}</dt>
        <dd className="break-words text-fg">
          {theirs}
          <span className="block text-xs text-fg-muted">“{other.evidence}”</span>
        </dd>
      </dl>
      {canChoose ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            className="h-8 px-2.5"
            aria-disabled={resolve.isPending || undefined}
            onClick={() => !resolve.isPending && resolve.mutate({ key: field.key, keep: "mine" })}
          >
            {t("requests.panel.conflict.keepMine")}
          </Button>
          <Button
            variant="secondary"
            className="h-8 px-2.5"
            aria-disabled={resolve.isPending || undefined}
            onClick={() => !resolve.isPending && resolve.mutate({ key: field.key, keep: "document" })}
          >
            {t("requests.panel.conflict.useDocument")}
          </Button>
        </div>
      ) : null}
      {resolve.isError ? (
        <p role="alert" className="mt-1 text-sm text-danger">
          {t("requests.panel.conflict.error")}
        </p>
      ) : null}
    </div>
  );
}
